/**
 * contextAdapter - 純粋関数モード用のcontextアダプター
 *
 * React setterをエミュレートし、cardEffects/cardTriggersを変更せずに
 * シミュレーションで使用できるようにする。
 *
 * @see src/ルール/context-adapter-analysis.md
 * @see src/ルール/simulation-full-integration-plan.md
 */

import {
  updatePlayer,
  addLog as addLogPure,
} from './GameState';
import type { GameState, PlayerState } from './GameState';

import {
  registerCardTriggersPure,
} from './triggerEnginePure';

import type { CardWithId, CardData, MonsterInstance, FieldCardInstance, PhaseCardInstance } from '../../types/card';
import type { LogEntry } from '../../types/game';
import type { PlayerNumber } from '../../types/player';
import type { DeepSeaZone, DeepSeaCard } from '../deepSeaZone/types';

// ========================================
// 型定義
// ========================================

/** Updater関数型 */
type Updater<T> = T | ((prev: T) => T);

/** 純粋コンテキストオプション */
export interface PureContextOptions {
  /** Effect perspective; the actual turn player is kept in GameState. */
  effectOwner?: PlayerNumber;
  monsterIndex?: number;
  skillType?: 'basic' | 'advanced';
  card?: CardWithId;
  slotIndex?: number;
  destroyedCard?: CardWithId;
  attackerIndex?: number;
  targetIndex?: number;
  attacker?: FieldMonster;
  target?: FieldMonster;
  leavingCard?: CardWithId;
  destroyedTarget?: FieldMonster;
  phaseCard?: CardWithId;
}

/** FieldMonster別名（後方互換） */
type FieldMonster = MonsterInstance;

/** モンスターターゲット選択オプション */
export interface MonsterTargetOptions {
  validTargets?: number[];
  isOpponent?: boolean;
  targetPlayer?: 'opponent' | 'self' | 'both';
  callback: (index: number) => void;
  message?: string;
}

/** デッキレビュー選択モード */
export interface DeckReviewSelectMode {
  enabled?: boolean;
  count?: number;
  filter?: (card: CardWithId) => boolean;
}

/** デッキレビューオプション */
export interface DeckReviewOptions {
  cards?: CardWithId[];
  selectMode?: DeckReviewSelectMode;
  allowReorder?: boolean;
  onSelect?: (selectedCards: CardWithId | CardWithId[], remaining?: CardWithId[]) => void;
  onConfirm?: (cards: CardWithId[]) => void;
  callback?: (cards: CardWithId[]) => void;
  message?: string;
  count?: number;
}

/** 手札選択オプション */
export interface HandSelectionOptions {
  count?: number;
  callback: (cards: CardWithId[]) => void;
  validCards?: CardWithId[];
  filter?: (card: CardWithId) => boolean;
  message?: string;
}

/** 墓地選択オプション */
export interface GraveyardSelectionOptions {
  cards?: CardWithId[];
  count?: number;
  filter?: (card: CardWithId) => boolean;
  callback: (cards: CardWithId[]) => void;
  targetPlayer?: PlayerNumber;
  selectionOwner?: PlayerNumber;
  message?: string;
}

/** 深海領域選択オプション */
export interface DeepSeaSelectionOptions {
  cards: DeepSeaCard[];
  count: number;
  callback: (selectedIndices: number[]) => void;
  message?: string;
}

/** 常時効果登録情報 */
interface ActiveEffectEntry {
  card: CardWithId;
  owner: PlayerNumber;
}

/** 常時効果エンジンインターフェース */
export interface ContinuousEffectEngineInterface {
  register: (card: CardWithId, owner: PlayerNumber) => void;
  unregister: (uniqueId: string) => void;
  clear: () => void;
}

/** 純粋コンテキストインターフェース */
export interface PureContext {
  // 現在のプレイヤー
  readonly currentPlayer: PlayerNumber;

  // オプションから渡されるプロパティ
  monsterIndex?: number;
  skillType?: 'basic' | 'advanced';
  card?: CardWithId;
  slotIndex?: number;
  destroyedCard?: CardWithId;
  attackerIndex?: number;
  targetIndex?: number;
  attacker?: FieldMonster;
  target?: FieldMonster;
  leavingCard?: CardWithId;
  destroyedTarget?: FieldMonster;
  phaseCard?: CardWithId;

  // P1 読み取りプロパティ
  readonly p1Life: number;
  readonly p1Field: (FieldMonster | null)[];
  readonly p1Hand: CardWithId[];
  readonly p1Deck: CardWithId[];
  readonly p1Graveyard: CardWithId[];
  readonly p1ActiveSP: number;
  readonly p1RestedSP: number;
  readonly p1FieldCard: FieldCardInstance | CardData | null;
  readonly p1PhaseCard: PhaseCardInstance | null;
  readonly p1StatusEffects: PlayerState['statusEffects'];

  // P2 読み取りプロパティ
  readonly p2Life: number;
  readonly p2Field: (FieldMonster | null)[];
  readonly p2Hand: CardWithId[];
  readonly p2Deck: CardWithId[];
  readonly p2Graveyard: CardWithId[];
  readonly p2ActiveSP: number;
  readonly p2RestedSP: number;
  readonly p2FieldCard: FieldCardInstance | CardData | null;
  readonly p2PhaseCard: PhaseCardInstance | null;
  readonly p2StatusEffects: PlayerState['statusEffects'];

  // P1 深海領域
  readonly p1DeepSeaZone: DeepSeaZone;
  // P2 深海領域
  readonly p2DeepSeaZone: DeepSeaZone;

  // P1 Setter
  setP1Life: (updater: Updater<number>) => void;
  setP1Field: (updater: Updater<(FieldMonster | null)[]>) => void;
  setP1Hand: (updater: Updater<CardWithId[]>) => void;
  setP1Deck: (updater: Updater<CardWithId[]>) => void;
  setP1Graveyard: (updater: Updater<CardWithId[]>) => void;
  setP1ActiveSP: (updater: Updater<number>) => void;
  setP1RestedSP: (updater: Updater<number>) => void;
  setP1FieldCard: (updater: Updater<FieldCardInstance | CardData | null>) => void;
  setP1PhaseCard: (updater: Updater<PhaseCardInstance | null>) => void;
  setP1StatusEffects: (updater: Updater<PlayerState['statusEffects']>) => void;

  // P2 Setter
  setP2Life: (updater: Updater<number>) => void;
  setP2Field: (updater: Updater<(FieldMonster | null)[]>) => void;
  setP2Hand: (updater: Updater<CardWithId[]>) => void;
  setP2Deck: (updater: Updater<CardWithId[]>) => void;
  setP2Graveyard: (updater: Updater<CardWithId[]>) => void;
  setP2ActiveSP: (updater: Updater<number>) => void;
  setP2RestedSP: (updater: Updater<number>) => void;
  setP2FieldCard: (updater: Updater<FieldCardInstance | CardData | null>) => void;
  setP2PhaseCard: (updater: Updater<PhaseCardInstance | null>) => void;
  setP2StatusEffects: (updater: Updater<PlayerState['statusEffects']>) => void;

  // 深海領域 Setter
  setP1DeepSeaZone: (updater: Updater<DeepSeaZone>) => void;
  setP2DeepSeaZone: (updater: Updater<DeepSeaZone>) => void;

  // ログ
  addLog: (message: string, type?: LogEntry['type']) => void;

  // UI選択系
  setPendingMonsterTarget: (options: MonsterTargetOptions) => void;
  setPendingDeckReview: (options: DeckReviewOptions) => void;
  setPendingHandSelection: (options: HandSelectionOptions) => void;
  setPendingGraveyardSelection: (options: GraveyardSelectionOptions) => void;
  setPendingDeepSeaSelection: (options: DeepSeaSelectionOptions) => void;

  // 特殊プロパティ
  setP1MagicBlocked: (value: boolean) => void;
  setP2MagicBlocked: (value: boolean) => void;
  setShowGraveyardViewer: (value: boolean) => void;
  registerCardTriggers: (card: CardWithId, owner: PlayerNumber, slotIndex?: number | null) => void;
  continuousEffectEngine: ContinuousEffectEngineInterface;

  // 状態取得
  getState: () => GameState;
  getActiveEffects: () => Map<string, ActiveEffectEntry>;
}

/** getPlayerContext互換オブジェクト */
export interface PlayerContextFromAdapter {
  myField: (FieldMonster | null)[];
  opponentField: (FieldMonster | null)[];
  setMyField: (updater: Updater<(FieldMonster | null)[]>) => void;
  setOpponentField: (updater: Updater<(FieldMonster | null)[]>) => void;

  myHand: CardWithId[];
  opponentHand: CardWithId[];
  setMyHand: (updater: Updater<CardWithId[]>) => void;
  setOpponentHand: (updater: Updater<CardWithId[]>) => void;

  myDeck: CardWithId[];
  opponentDeck: CardWithId[];
  setMyDeck: (updater: Updater<CardWithId[]>) => void;
  setOpponentDeck: (updater: Updater<CardWithId[]>) => void;

  myGraveyard: CardWithId[];
  opponentGraveyard: CardWithId[];
  setMyGraveyard: (updater: Updater<CardWithId[]>) => void;
  setOpponentGraveyard: (updater: Updater<CardWithId[]>) => void;

  myLife: number;
  opponentLife: number;
  setMyLife: (updater: Updater<number>) => void;
  setOpponentLife: (updater: Updater<number>) => void;

  myActiveSP: number;
  opponentActiveSP: number;
  setMyActiveSP: (updater: Updater<number>) => void;
  setOpponentActiveSP: (updater: Updater<number>) => void;

  myRestedSP: number;
  opponentRestedSP: number;
  setMyRestedSP: (updater: Updater<number>) => void;
  setOpponentRestedSP: (updater: Updater<number>) => void;

  myFieldCard: FieldCardInstance | CardData | null;
  opponentFieldCard: FieldCardInstance | CardData | null;
  setMyFieldCard: (updater: Updater<FieldCardInstance | CardData | null>) => void;
  setOpponentFieldCard: (updater: Updater<FieldCardInstance | CardData | null>) => void;

  myPhaseCard: PhaseCardInstance | null;
  opponentPhaseCard: PhaseCardInstance | null;
  setMyPhaseCard: (updater: Updater<PhaseCardInstance | null>) => void;
  setOpponentPhaseCard: (updater: Updater<PhaseCardInstance | null>) => void;

  myStatusEffects: PlayerState['statusEffects'];
  opponentStatusEffects: PlayerState['statusEffects'];
  setMyStatusEffects: (updater: Updater<PlayerState['statusEffects']>) => void;
  setOpponentStatusEffects: (updater: Updater<PlayerState['statusEffects']>) => void;

  myDeepSeaZone: DeepSeaZone;
  opponentDeepSeaZone: DeepSeaZone;
  setMyDeepSeaZone: (updater: Updater<DeepSeaZone>) => void;
  setOpponentDeepSeaZone: (updater: Updater<DeepSeaZone>) => void;

  isP1: boolean;
  currentPlayer: PlayerNumber;
}

// ========================================
// メインアダプター
// ========================================

/**
 * 純粋関数モード用のcontextを作成
 *
 * cardEffects/*.ts, cardTriggers/*.ts が期待するcontextオブジェクトを生成。
 * React setterの代わりに内部でstateを更新し、getState()で最終stateを取得。
 */
export function createPureContext(initialState: GameState, options: PureContextOptions = {}): PureContext {
  // Legacy status helpers mutate the supplied array, so give them owned copies.
  let currentState: GameState = {
    ...initialState,
    p1: { ...initialState.p1, statusEffects: initialState.p1.statusEffects.map(status => ({ ...status })) },
    p2: { ...initialState.p2, statusEffects: initialState.p2.statusEffects.map(status => ({ ...status })) },
  };

  // 常時効果のアクティブ効果を管理（シミュレーション用）
  const activeEffects = new Map<string, ActiveEffectEntry>();

  // ========================================
  // ヘルパー関数
  // ========================================

  const getCurrentPlayer = (): PlayerNumber => options.effectOwner ?? currentState.currentPlayer;
  const isP1 = (): boolean => getCurrentPlayer() === 1;

  /**
   * プレイヤー状態を更新
   */
  const updatePlayerState = (playerNum: PlayerNumber, updates: Partial<PlayerState>): void => {
    currentState = updatePlayer(currentState, playerNum, updates);
  };

  /**
   * updater関数を解決
   */
  const resolveUpdater = <T>(updater: Updater<T>, currentValue: T): T => {
    return typeof updater === 'function' ? (updater as (prev: T) => T)(currentValue) : updater;
  };

  // ========================================
  // 基本プロパティ
  // ========================================

  const context = {
    // 現在のプレイヤー
    get currentPlayer() {
      return getCurrentPlayer();
    },

    // オプションから渡されるプロパティ
    monsterIndex: options.monsterIndex,
    skillType: options.skillType,
    card: options.card,
    slotIndex: options.slotIndex,
    destroyedCard: options.destroyedCard,
    attackerIndex: options.attackerIndex,
    targetIndex: options.targetIndex,
    attacker: options.attacker,
    target: options.target,
    leavingCard: options.leavingCard,
    destroyedTarget: options.destroyedTarget,
    phaseCard: options.phaseCard,

    // ========================================
    // P1 読み取りプロパティ
    // ========================================

    get p1Life() {
      return currentState.p1.life;
    },
    get p1Field() {
      return currentState.p1.field;
    },
    get p1Hand() {
      return currentState.p1.hand;
    },
    get p1Deck() {
      return currentState.p1.deck;
    },
    get p1Graveyard() {
      return currentState.p1.graveyard;
    },
    get p1ActiveSP() {
      return currentState.p1.activeSP;
    },
    get p1RestedSP() {
      return currentState.p1.restedSP;
    },
    get p1FieldCard() {
      return currentState.p1.fieldCard;
    },
    get p1PhaseCard() {
      return currentState.p1.phaseCard;
    },
    get p1StatusEffects() {
      return currentState.p1.statusEffects;
    },

    // ========================================
    // P2 読み取りプロパティ
    // ========================================

    get p2Life() {
      return currentState.p2.life;
    },
    get p2Field() {
      return currentState.p2.field;
    },
    get p2Hand() {
      return currentState.p2.hand;
    },
    get p2Deck() {
      return currentState.p2.deck;
    },
    get p2Graveyard() {
      return currentState.p2.graveyard;
    },
    get p2ActiveSP() {
      return currentState.p2.activeSP;
    },
    get p2RestedSP() {
      return currentState.p2.restedSP;
    },
    get p2FieldCard() {
      return currentState.p2.fieldCard;
    },
    get p2PhaseCard() {
      return currentState.p2.phaseCard;
    },
    get p2StatusEffects() {
      return currentState.p2.statusEffects;
    },

    // ========================================
    // 深海領域 読み取りプロパティ
    // ========================================

    get p1DeepSeaZone() {
      return currentState.p1.deepSeaZone;
    },
    get p2DeepSeaZone() {
      return currentState.p2.deepSeaZone;
    },

    // ========================================
    // P1 Setter
    // ========================================

    setP1Life: (updater: Updater<number>): void => {
      const newValue = resolveUpdater(updater, currentState.p1.life);
      updatePlayerState(1, { life: newValue });
    },

    setP1Field: (updater: Updater<(FieldMonster | null)[]>): void => {
      const newValue = resolveUpdater(updater, currentState.p1.field);
      updatePlayerState(1, { field: newValue });
    },

    setP1Hand: (updater: Updater<CardWithId[]>): void => {
      const newValue = resolveUpdater(updater, currentState.p1.hand);
      updatePlayerState(1, { hand: newValue });
    },

    setP1Deck: (updater: Updater<CardWithId[]>): void => {
      const newValue = resolveUpdater(updater, currentState.p1.deck);
      updatePlayerState(1, { deck: newValue });
    },

    setP1Graveyard: (updater: Updater<CardWithId[]>): void => {
      const newValue = resolveUpdater(updater, currentState.p1.graveyard);
      updatePlayerState(1, { graveyard: newValue });
    },

    setP1ActiveSP: (updater: Updater<number>): void => {
      const newValue = resolveUpdater(updater, currentState.p1.activeSP);
      updatePlayerState(1, { activeSP: newValue });
    },

    setP1RestedSP: (updater: Updater<number>): void => {
      const newValue = resolveUpdater(updater, currentState.p1.restedSP);
      updatePlayerState(1, { restedSP: newValue });
    },

    setP1FieldCard: (updater: Updater<FieldCardInstance | CardData | null>): void => {
      const newValue = resolveUpdater(updater, currentState.p1.fieldCard);
      updatePlayerState(1, { fieldCard: newValue });
    },

    setP1PhaseCard: (updater: Updater<PhaseCardInstance | null>): void => {
      const newValue = resolveUpdater(updater, currentState.p1.phaseCard);
      updatePlayerState(1, { phaseCard: newValue });
    },

    setP1StatusEffects: (updater: Updater<PlayerState['statusEffects']>): void => {
      const newValue = resolveUpdater(updater, currentState.p1.statusEffects);
      updatePlayerState(1, { statusEffects: newValue });
    },

    // ========================================
    // P2 Setter
    // ========================================

    setP2Life: (updater: Updater<number>): void => {
      const newValue = resolveUpdater(updater, currentState.p2.life);
      updatePlayerState(2, { life: newValue });
    },

    setP2Field: (updater: Updater<(FieldMonster | null)[]>): void => {
      const newValue = resolveUpdater(updater, currentState.p2.field);
      updatePlayerState(2, { field: newValue });
    },

    setP2Hand: (updater: Updater<CardWithId[]>): void => {
      const newValue = resolveUpdater(updater, currentState.p2.hand);
      updatePlayerState(2, { hand: newValue });
    },

    setP2Deck: (updater: Updater<CardWithId[]>): void => {
      const newValue = resolveUpdater(updater, currentState.p2.deck);
      updatePlayerState(2, { deck: newValue });
    },

    setP2Graveyard: (updater: Updater<CardWithId[]>): void => {
      const newValue = resolveUpdater(updater, currentState.p2.graveyard);
      updatePlayerState(2, { graveyard: newValue });
    },

    setP2ActiveSP: (updater: Updater<number>): void => {
      const newValue = resolveUpdater(updater, currentState.p2.activeSP);
      updatePlayerState(2, { activeSP: newValue });
    },

    setP2RestedSP: (updater: Updater<number>): void => {
      const newValue = resolveUpdater(updater, currentState.p2.restedSP);
      updatePlayerState(2, { restedSP: newValue });
    },

    setP2FieldCard: (updater: Updater<FieldCardInstance | CardData | null>): void => {
      const newValue = resolveUpdater(updater, currentState.p2.fieldCard);
      updatePlayerState(2, { fieldCard: newValue });
    },

    setP2PhaseCard: (updater: Updater<PhaseCardInstance | null>): void => {
      const newValue = resolveUpdater(updater, currentState.p2.phaseCard);
      updatePlayerState(2, { phaseCard: newValue });
    },

    setP2StatusEffects: (updater: Updater<PlayerState['statusEffects']>): void => {
      const newValue = resolveUpdater(updater, currentState.p2.statusEffects);
      updatePlayerState(2, { statusEffects: newValue });
    },

    // ========================================
    // 深海領域 Setter
    // ========================================

    setP1DeepSeaZone: (updater: Updater<DeepSeaZone>): void => {
      const newValue = resolveUpdater(updater, currentState.p1.deepSeaZone);
      updatePlayerState(1, { deepSeaZone: newValue });
    },

    setP2DeepSeaZone: (updater: Updater<DeepSeaZone>): void => {
      const newValue = resolveUpdater(updater, currentState.p2.deepSeaZone);
      updatePlayerState(2, { deepSeaZone: newValue });
    },

    // ========================================
    // ログ
    // ========================================

    addLog: (message: string, type: LogEntry['type'] = 'info'): void => {
      currentState = addLogPure(currentState, message, type);
    },

    // ========================================
    // UI選択系（自動選択）
    // ========================================

    /**
     * デッキからカード選択（自動選択）
     * - selectMode.enabled: true → onSelect(selectedCards) で選択結果を返す
     * - allowReorder: true → onConfirm(cards) でそのまま返す（シミュレーションでは並び替えなし）
     * - callback → callback(bestCard) で最高コストのカードを返す（後方互換）
     */
    setPendingDeckReview: (options: DeckReviewOptions): void => {
      const { cards, selectMode, allowReorder, onSelect, onConfirm, callback } = options;

      if (!cards || cards.length === 0) {
        // 空の場合は警告を出力
        const sourceCard = context.card;
        console.warn('[Simulator] setPendingDeckReview: カードがありません', {
          sourceCard: sourceCard ? `${sourceCard.name} (${sourceCard.id})` : 'unknown',
          uiMessage: options.message,
        });
        if (onSelect) {
          onSelect([]);
        } else if (onConfirm) {
          onConfirm([]);
        } else if (callback) {
          callback([]);  // nullではなく空配列を返す（カード効果が配列を期待するため）
        }
        return;
      }

      // 選択モード（selectMode.enabled）
      if (selectMode && selectMode.enabled && onSelect) {
        const count = selectMode.count || 1;
        const filter = selectMode.filter;

        // フィルター条件がある場合は適用
        let targetCards = cards;
        if (filter) {
          targetCards = cards.filter(filter);
        }

        // コストが最も高いカードをcount枚選択
        const sorted = [...targetCards].sort((a, b) => (b.cost || 0) - (a.cost || 0));
        const selected = sorted.slice(0, Math.min(count, sorted.length));

        // 残りのカード
        const selectedIds = new Set(selected.map(c => c.uniqueId || c.id));
        const remaining = cards.filter(c => !selectedIds.has(c.uniqueId || c.id));

        // onSelectコールバック - DeckReviewModalと同じインターフェース
        // 常に配列を渡す: onSelect(selectedCards[], remainingCards[])
        onSelect(selected, remaining);
        return;
      }

      // 並び替えモード（allowReorder）
      if (allowReorder && onConfirm) {
        // シミュレーションでは並び替えなしでそのまま返す
        onConfirm(cards);
        return;
      }

      // 後方互換: callback（配列を返す）
      if (callback) {
        // count指定がある場合はその数だけ選択
        const selectCount = options.count || 1;

        // コストが最も高いカード順にソート
        const sorted = [...cards].sort((a, b) => (b.cost || 0) - (a.cost || 0));

        // 選択した配列を返す（card effectsが配列を期待するため）
        const selected = sorted.slice(0, Math.min(selectCount, sorted.length));
        callback(selected);
        return;
      }

      // onSelectのみでselectModeなしの場合も対応
      // DeckReviewModalと同じインターフェース: 配列を渡す
      if (onSelect) {
        const sorted = [...cards].sort((a, b) => (b.cost || 0) - (a.cost || 0));
        onSelect([sorted[0]], sorted.slice(1));
      }
    },

    /**
     * 手札選択（自動選択）
     * カード効果が期待するのはカードオブジェクト（インデックスではない）
     */
    setPendingHandSelection: (selectOptions: HandSelectionOptions): void => {
      const { count = 1, callback, validCards, filter } = selectOptions;

      const hand = isP1() ? currentState.p1.hand : currentState.p2.hand;
      let targetCards = validCards || hand;

      // フィルターがある場合は適用
      if (filter && targetCards) {
        targetCards = targetCards.filter(filter);
      }

      if (!targetCards || targetCards.length === 0) {
        // カードが見つからない場合は警告を出力してスキップ
        // 開発中はこの警告で効果が発動できていない状況を検知できる
        const sourceCard = context.card;
        console.warn('[Simulator] setPendingHandSelection: 有効なカードがありません', {
          sourceCard: sourceCard ? `${sourceCard.name} (${sourceCard.id})` : 'unknown',
          uiMessage: selectOptions.message,
          filterApplied: !!filter,
          handSize: hand?.length || 0,
        });
        return;
      }

      // コストが高い順（召喚/配置）またはコストが低い順（捨てる）でソート
      // 召喚系の場合はコストが高い方が有用なのでコスト降順
      const sorted = [...targetCards].sort((a, b) => (b.cost || 0) - (a.cost || 0));

      // 常に配列を返す（多くのcardEffectsが配列を期待している）
      // count=1でも1要素の配列を返すことで一貫性を保つ
      callback(sorted.slice(0, count));
    },

    /**
     * モンスター対象選択（自動選択）
     * validTargetsがない場合はtargetPlayerからフィールドを参照して自動計算
     */
    setPendingMonsterTarget: (targetOptions: MonsterTargetOptions): void => {
      const { validTargets, targetPlayer, callback, message } = targetOptions;

      // validTargetsが指定されている場合はそれを使用
      let targets = validTargets;

      // validTargetsがない場合はtargetPlayerからフィールドを参照
      if (!targets || targets.length === 0) {
        const myField = isP1() ? currentState.p1.field : currentState.p2.field;
        const opponentField = isP1() ? currentState.p2.field : currentState.p1.field;

        let targetField: (FieldMonster | null)[] = [];
        if (targetPlayer === 'opponent') {
          targetField = opponentField;
        } else if (targetPlayer === 'self') {
          targetField = myField;
        } else if (targetPlayer === 'both') {
          // 両方の場合は相手を優先
          targetField = [...opponentField, ...myField];
        }

        // モンスターがいるスロットのインデックスを取得
        targets = targetField
          .map((m, idx) => ({ monster: m, index: idx }))
          .filter(({ monster }) => monster !== null)
          .map(({ index }) => index);
      }

      if (!targets || targets.length === 0) {
        const sourceCard = context.card;
        console.warn('[Simulator] setPendingMonsterTarget: 有効なターゲットがありません', {
          sourceCard: sourceCard ? `${sourceCard.name} (${sourceCard.id})` : 'unknown',
          uiMessage: message,
          targetPlayer,
        });
        callback(-1);
        return;
      }

      // 最初のターゲットを選択（AIは単純に最初のモンスターを選ぶ）
      callback(targets[0]);
    },

    /**
     * 墓地からカード選択（自動選択）
     * コストが最も高いモンスターを優先
     */
    setPendingGraveyardSelection: (graveyardOptions: GraveyardSelectionOptions): void => {
      const { cards, count = 1, filter, callback, message } = graveyardOptions;
      const targetPlayer = graveyardOptions.targetPlayer ?? graveyardOptions.selectionOwner ?? getCurrentPlayer();

      const graveyard = targetPlayer === 1
        ? currentState.p1.graveyard
        : currentState.p2.graveyard;

      const targetCards = (cards ?? graveyard).filter(card => card != null && (!filter || filter(card)));

      if (!targetCards || targetCards.length === 0) {
        const sourceCard = context.card;
        console.warn('[Simulator] setPendingGraveyardSelection: 墓地に有効なカードがありません', {
          sourceCard: sourceCard ? `${sourceCard.name} (${sourceCard.id})` : 'unknown',
          uiMessage: message,
          graveyardSize: graveyard?.length || 0,
        });
        callback([]);
        return;
      }

      // Match the UI contract, including effects that recover multiple cards.
      const selected = [...targetCards]
        .sort((a, b) => (b.cost || 0) - (a.cost || 0))
        .slice(0, Math.max(0, count));
      callback(selected);
    },

    /**
     * 深海領域からカード選択（自動選択）
     * 深度が最も高いカードを優先（深度ボーナスが大きい）
     */
    setPendingDeepSeaSelection: (deepSeaOptions: DeepSeaSelectionOptions): void => {
      const { cards, count = 1, callback, message } = deepSeaOptions;

      if (!cards || cards.length === 0) {
        const sourceCard = context.card;
        console.warn('[Simulator] setPendingDeepSeaSelection: 深海領域に有効なカードがありません', {
          sourceCard: sourceCard ? `${sourceCard.name} (${sourceCard.id})` : 'unknown',
          uiMessage: message,
        });
        callback([]);
        return;
      }

      // 深度が高い順にソート
      const sortedIndices = cards
        .map((card, index) => ({ card, index }))
        .sort((a, b) => b.card.depth - a.card.depth)
        .slice(0, Math.min(count, cards.length))
        .map(item => item.index);

      callback(sortedIndices);
    },

    // ========================================
    // 特殊プロパティ
    // ========================================

    /**
     * 魔法ブロック（P1）
     */
    setP1MagicBlocked: (value: boolean): void => {
      updatePlayerState(1, { magicBlocked: value });
    },

    /**
     * 魔法ブロック（P2）
     */
    setP2MagicBlocked: (value: boolean): void => {
      updatePlayerState(2, { magicBlocked: value });
    },

    /**
     * 墓地ビューア表示（シミュレーションでは無視）
     */
    setShowGraveyardViewer: (_value: boolean): void => {
      // シミュレーションではUI不要、何もしない
    },

    /**
     * トリガー登録（Pure版へディスパッチ）
     */
    registerCardTriggers: (card: CardWithId, owner: PlayerNumber, slotIndex: number | null = null): void => {
      currentState = registerCardTriggersPure(currentState, card, owner, slotIndex);
    },

    /**
     * 常時効果エンジン（簡易実装）
     */
    continuousEffectEngine: {
      register: (card: CardWithId, owner: PlayerNumber): void => {
        if (card && card.uniqueId) {
          activeEffects.set(card.uniqueId, { card, owner });
        }
      },
      unregister: (uniqueId: string): void => {
        activeEffects.delete(uniqueId);
      },
      clear: (): void => {
        activeEffects.clear();
      },
    },

    // ========================================
    // 状態取得
    // ========================================

    /**
     * 現在のstateを取得
     */
    getState: () => currentState,

    /**
     * アクティブ効果を取得
     */
    getActiveEffects: () => activeEffects,
  };

  return context;
}

// ========================================
// getPlayerContext互換関数
// ========================================

/**
 * getPlayerContext互換のプロパティを取得
 *
 * effectHelpers.tsのgetPlayerContext()と同じ構造を返す。
 * contextAdapter内で使用する場合は、context自体がこれらのプロパティを持つ。
 */
export function getPlayerContextFromAdapter(context: PureContext): PlayerContextFromAdapter {
  const currentPlayer: PlayerNumber = context.currentPlayer;
  const isP1: boolean = currentPlayer === 1;

  return {
    // === フィールド ===
    myField: isP1 ? context.p1Field : context.p2Field,
    opponentField: isP1 ? context.p2Field : context.p1Field,
    setMyField: isP1 ? context.setP1Field : context.setP2Field,
    setOpponentField: isP1 ? context.setP2Field : context.setP1Field,

    // === 手札 ===
    myHand: isP1 ? context.p1Hand : context.p2Hand,
    opponentHand: isP1 ? context.p2Hand : context.p1Hand,
    setMyHand: isP1 ? context.setP1Hand : context.setP2Hand,
    setOpponentHand: isP1 ? context.setP2Hand : context.setP1Hand,

    // === デッキ ===
    myDeck: isP1 ? context.p1Deck : context.p2Deck,
    opponentDeck: isP1 ? context.p2Deck : context.p1Deck,
    setMyDeck: isP1 ? context.setP1Deck : context.setP2Deck,
    setOpponentDeck: isP1 ? context.setP2Deck : context.setP1Deck,

    // === 墓地 ===
    myGraveyard: isP1 ? context.p1Graveyard : context.p2Graveyard,
    opponentGraveyard: isP1 ? context.p2Graveyard : context.p1Graveyard,
    setMyGraveyard: isP1 ? context.setP1Graveyard : context.setP2Graveyard,
    setOpponentGraveyard: isP1 ? context.setP2Graveyard : context.setP1Graveyard,

    // === ライフ ===
    myLife: isP1 ? context.p1Life : context.p2Life,
    opponentLife: isP1 ? context.p2Life : context.p1Life,
    setMyLife: isP1 ? context.setP1Life : context.setP2Life,
    setOpponentLife: isP1 ? context.setP2Life : context.setP1Life,

    // === SP（アクティブ） ===
    myActiveSP: isP1 ? context.p1ActiveSP : context.p2ActiveSP,
    opponentActiveSP: isP1 ? context.p2ActiveSP : context.p1ActiveSP,
    setMyActiveSP: isP1 ? context.setP1ActiveSP : context.setP2ActiveSP,
    setOpponentActiveSP: isP1 ? context.setP2ActiveSP : context.setP1ActiveSP,

    // === SP（レスト） ===
    myRestedSP: isP1 ? context.p1RestedSP : context.p2RestedSP,
    opponentRestedSP: isP1 ? context.p2RestedSP : context.p1RestedSP,
    setMyRestedSP: isP1 ? context.setP1RestedSP : context.setP2RestedSP,
    setOpponentRestedSP: isP1 ? context.setP2RestedSP : context.setP1RestedSP,

    // === フィールドカード ===
    myFieldCard: isP1 ? context.p1FieldCard : context.p2FieldCard,
    opponentFieldCard: isP1 ? context.p2FieldCard : context.p1FieldCard,
    setMyFieldCard: isP1 ? context.setP1FieldCard : context.setP2FieldCard,
    setOpponentFieldCard: isP1 ? context.setP2FieldCard : context.setP1FieldCard,

    // === フェイズカード ===
    myPhaseCard: isP1 ? context.p1PhaseCard : context.p2PhaseCard,
    opponentPhaseCard: isP1 ? context.p2PhaseCard : context.p1PhaseCard,
    setMyPhaseCard: isP1 ? context.setP1PhaseCard : context.setP2PhaseCard,
    setOpponentPhaseCard: isP1 ? context.setP2PhaseCard : context.setP1PhaseCard,

    // === 状態異常（プレイヤー） ===
    myStatusEffects: isP1 ? context.p1StatusEffects : context.p2StatusEffects,
    opponentStatusEffects: isP1 ? context.p2StatusEffects : context.p1StatusEffects,
    setMyStatusEffects: isP1 ? context.setP1StatusEffects : context.setP2StatusEffects,
    setOpponentStatusEffects: isP1 ? context.setP2StatusEffects : context.setP1StatusEffects,

    // === 深海領域 ===
    myDeepSeaZone: isP1 ? context.p1DeepSeaZone : context.p2DeepSeaZone,
    opponentDeepSeaZone: isP1 ? context.p2DeepSeaZone : context.p1DeepSeaZone,
    setMyDeepSeaZone: isP1 ? context.setP1DeepSeaZone : context.setP2DeepSeaZone,
    setOpponentDeepSeaZone: isP1 ? context.setP2DeepSeaZone : context.setP1DeepSeaZone,

    // === ユーティリティ ===
    isP1,
    currentPlayer,
  };
}

export default createPureContext;
